/**
 * Infrastructure Store — Zustand + immer
 *
 * Manages infrastructure host and VM state for the dashboard.
 * Uses the API layer from infrastructureApi.ts.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import * as infraApi from '@/services/api/infrastructureApi';
import type {
  InfraHost,
  VMInfo,
  HostResources,
  TemplateInfo,
  NetworkInfo,
  StorageRepo,
} from '@/services/api/infrastructureApi';

interface InfraStoreState {
  hosts: InfraHost[];
  selectedHostId: string | null;
  vms: VMInfo[];
  resources: HostResources | null;
  templates: TemplateInfo[];
  networks: NetworkInfo[];
  storage: StorageRepo[];
  isLoading: boolean;
  isLoadingVMs: boolean;
  error: string | null;
}

interface InfraStoreActions {
  // Host management
  fetchHosts: () => Promise<void>;
  selectHost: (hostId: string | null) => void;
  registerHost: (data: Parameters<typeof infraApi.registerHost>[0]) => Promise<InfraHost | null>;
  deleteHost: (hostId: string) => Promise<boolean>;

  updateHost: (hostId: string, updates: Partial<InfraHost>) => Promise<boolean>;
  transferHost: (hostId: string, targetOrgSlug: string) => Promise<boolean>;

  // Resource discovery
  discoverResources: (hostId: string) => Promise<HostResources | null>;

  // VM operations
  fetchVMs: (hostId: string) => Promise<void>;
  createVM: (hostId: string, spec: infraApi.VMCreateSpec) => Promise<VMInfo | null>;
  startVM: (hostId: string, vmId: string) => Promise<boolean>;
  stopVM: (hostId: string, vmId: string, force?: boolean) => Promise<boolean>;
  destroyVM: (hostId: string, vmId: string) => Promise<boolean>;
  snapshotVM: (hostId: string, vmId: string, name: string) => Promise<boolean>;

  // Host resource queries
  fetchTemplates: (hostId: string) => Promise<void>;
  fetchNetworks: (hostId: string) => Promise<void>;
  fetchStorage: (hostId: string) => Promise<void>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

const getErrorMessage = (error: unknown, fallback = 'Infrastructure request failed'): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const initialState: InfraStoreState = {
  hosts: [],
  selectedHostId: null,
  vms: [],
  resources: null,
  templates: [],
  networks: [],
  storage: [],
  isLoading: false,
  isLoadingVMs: false,
  error: null,
};

export const useInfraStore = create<InfraStoreState & InfraStoreActions>()(
  immer((set, get) => ({
    ...initialState,

    // ─── Host Management ──────────────────────────────────────

    fetchHosts: async () => {
      set((s) => { s.isLoading = true; s.error = null; });
      try {
        const res = await infraApi.listHosts();
        set((s) => { s.hosts = res.data || []; s.isLoading = false; });
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); s.isLoading = false; });
      }
    },

    selectHost: (hostId) => {
      set((s) => {
        s.selectedHostId = hostId;
        s.vms = [];
        s.resources = null;
        s.templates = [];
        s.networks = [];
        s.storage = [];
      });
      if (hostId) {
        get().fetchVMs(hostId);
        get().discoverResources(hostId);
      }
    },

    registerHost: async (data) => {
      set((s) => { s.isLoading = true; s.error = null; });
      try {
        const res = await infraApi.registerHost(data);
        const host = res.data;
        if (host) {
          set((s) => { s.hosts.push(host); s.isLoading = false; });
        }
        return host || null;
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); s.isLoading = false; });
        return null;
      }
    },

    updateHost: async (hostId, updates) => {
      try {
        const res = await infraApi.updateHost(hostId, updates);
        const updated = res.data;
        if (updated) {
          set((s) => {
            const idx = s.hosts.findIndex((h) => h.id === hostId);
            if (idx !== -1) s.hosts[idx] = updated;
          });
        }
        return true;
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); });
        return false;
      }
    },

    transferHost: async (hostId, targetOrgSlug) => {
      try {
        await infraApi.transferHost(hostId, targetOrgSlug);
        // Host moved to different org — remove from current list
        set((s) => {
          s.hosts = s.hosts.filter((h) => h.id !== hostId);
          if (s.selectedHostId === hostId) {
            s.selectedHostId = null;
            s.vms = [];
            s.resources = null;
          }
        });
        return true;
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); });
        return false;
      }
    },

    deleteHost: async (hostId) => {
      try {
        await infraApi.deleteHost(hostId);
        set((s) => {
          s.hosts = s.hosts.filter((h) => h.id !== hostId);
          if (s.selectedHostId === hostId) {
            s.selectedHostId = null;
            s.vms = [];
            s.resources = null;
          }
        });
        return true;
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); });
        return false;
      }
    },

    // ─── Resource Discovery ───────────────────────────────────

    discoverResources: async (hostId) => {
      try {
        const res = await infraApi.discoverResources(hostId);
        const resources = res.data || null;
        set((s) => { s.resources = resources; });
        return resources;
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); });
        return null;
      }
    },

    // ─── VM Operations ────────────────────────────────────────

    fetchVMs: async (hostId) => {
      set((s) => { s.isLoadingVMs = true; });
      try {
        const res = await infraApi.listVMs(hostId);
        set((s) => { s.vms = res.data || []; s.isLoadingVMs = false; });
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); s.isLoadingVMs = false; });
      }
    },

    createVM: async (hostId, spec) => {
      set((s) => { s.isLoading = true; s.error = null; });
      try {
        const res = await infraApi.createVM(hostId, spec);
        const vm = res.data;
        if (vm) {
          set((s) => { s.vms.push(vm); s.isLoading = false; });
        }
        return vm || null;
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); s.isLoading = false; });
        return null;
      }
    },

    startVM: async (hostId, vmId) => {
      try {
        await infraApi.startVM(hostId, vmId);
        set((s) => {
          const vm = s.vms.find((v) => v.id === vmId);
          if (vm) vm.state = 'running';
        });
        return true;
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); });
        return false;
      }
    },

    stopVM: async (hostId, vmId, force) => {
      try {
        await infraApi.stopVM(hostId, vmId, force);
        set((s) => {
          const vm = s.vms.find((v) => v.id === vmId);
          if (vm) vm.state = 'stopped';
        });
        return true;
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); });
        return false;
      }
    },

    destroyVM: async (hostId, vmId) => {
      try {
        await infraApi.destroyVM(hostId, vmId);
        set((s) => { s.vms = s.vms.filter((v) => v.id !== vmId); });
        return true;
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); });
        return false;
      }
    },

    snapshotVM: async (hostId, vmId, name) => {
      try {
        await infraApi.snapshotVM(hostId, vmId, name);
        return true;
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); });
        return false;
      }
    },

    // ─── Host Resource Queries ────────────────────────────────

    fetchTemplates: async (hostId) => {
      try {
        const res = await infraApi.listTemplates(hostId);
        set((s) => { s.templates = res.data || []; });
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); });
      }
    },

    fetchNetworks: async (hostId) => {
      try {
        const res = await infraApi.listNetworks(hostId);
        set((s) => { s.networks = res.data || []; });
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); });
      }
    },

    fetchStorage: async (hostId) => {
      try {
        const res = await infraApi.listStorage(hostId);
        set((s) => { s.storage = res.data || []; });
      } catch (err: unknown) {
        set((s) => { s.error = getErrorMessage(err); });
      }
    },

    // ─── Utility ──────────────────────────────────────────────

    clearError: () => set((s) => { s.error = null; }),
    reset: () => set(() => ({ ...initialState })),
  }))
);

// Reset on logout to prevent data leakage between sessions
if (typeof window !== 'undefined') {
  import('../auth/auth-store').then(({ useAuthStore }) => {
    useAuthStore.subscribe(
      (state) => state.isAuthenticated,
      (isAuthenticated, prev) => {
        if (prev && !isAuthenticated) {
          useInfraStore.getState().reset();
        }
      }
    );
  });
}
